import { useCallback, useEffect, useState } from 'react';
import { Check, Clock, Copy, FolderCheck, Link2, Plus, Trash2 } from 'lucide-react';
import {
  linkRaccolta,
  useDocumentCollectionStore,
  type RaccoltaDocumenti,
} from '../../lib/stores/documentCollectionStore';
import { SignedUrl } from '../shared/SignedUrl';
import RichiediDocumentiModal from './RichiediDocumentiModal';

/**
 * Documenti richiesti: cosa e' arrivato, cosa manca, e il collegamento da
 * rimandare a chi deve ancora consegnare.
 *
 * Vale per un cliente, un progetto o un'offerta: gli stessi agganci dei
 * documenti che la raccolta contiene. E' la stessa cosa vista dal lato di chi
 * aspetta, e i file che arrivano diventano documenti normali del CRM.
 */

export interface AggancioRaccolta {
  client_id?: string;
  project_id?: string;
  deal_id?: string;
}

interface Props {
  aggancio: AggancioRaccolta;
  /** Titolo della sezione; cambia solo se il contesto lo richiede. */
  titolo?: string;
}

export default function DocumentCollectionsSection({ aggancio, titolo = 'Documenti richiesti' }: Props) {
  const { raccolte, carica, segnaVerificato, rinnova, elimina } = useDocumentCollectionStore();
  const [modaleAperto, setModaleAperto] = useState(false);
  const [copiato, setCopiato] = useState<string | null>(null);

  const chiave = JSON.stringify(aggancio);
  useEffect(() => {
    void carica(JSON.parse(chiave));
  }, [chiave, carica]);

  const copiaLink = useCallback(async (token: string) => {
    try {
      await navigator.clipboard.writeText(linkRaccolta(token));
      setCopiato(token);
      setTimeout(() => setCopiato(null), 2000);
    } catch {
      window.prompt('Copia il collegamento:', linkRaccolta(token));
    }
  }, []);

  return (
    <>
      <div className="flex items-center justify-between py-2 mb-1">
        <div className="flex items-center space-x-2">
          <h3 className="text-xs font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wide">
            {titolo}
          </h3>
          {raccolte.length > 0 && (
            <span className="text-xs text-[#787774] dark:text-gray-500">({raccolte.length})</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setModaleAperto(true)}
          className="text-xs text-[#337EA9] dark:text-[#529CCA] hover:underline flex items-center gap-0.5"
        >
          <Plus className="h-3 w-3" />
          Richiedi
        </button>
      </div>

      {raccolte.length === 0 ? (
        <p className="px-2 pb-2 text-xs text-[#9B9A97]">
          Nessuna richiesta aperta. "Richiedi" crea un collegamento da mandare al cliente.
        </p>
      ) : (
        <div className="space-y-3">
          {raccolte.map((raccolta) => (
            <Raccolta
              key={raccolta.id}
              raccolta={raccolta}
              copiato={copiato === raccolta.token}
              onCopia={() => void copiaLink(raccolta.token)}
              onVerifica={(documentoId, verificato) => void segnaVerificato(documentoId, verificato)}
              onRinnova={() => void rinnova(raccolta.id, 30)}
              onElimina={() => void elimina(raccolta.id)}
            />
          ))}
        </div>
      )}

      {modaleAperto && (
        <RichiediDocumentiModal
          aggancio={aggancio}
          onChiudi={() => setModaleAperto(false)}
        />
      )}
    </>
  );
}

function Raccolta({
  raccolta,
  copiato,
  onCopia,
  onVerifica,
  onRinnova,
  onElimina,
}: {
  raccolta: RaccoltaDocumenti;
  copiato: boolean;
  onCopia: () => void;
  onVerifica: (documentoId: string, verificato: boolean) => void;
  onRinnova: () => void;
  onElimina: () => void;
}) {
  const obbligatorie = raccolta.voci.filter((v) => v.required);
  const fatte = obbligatorie.filter((v) =>
    raccolta.documenti.some((d) => d.requirement_id === v.id),
  ).length;
  const scaduta = !!raccolta.expires_at && new Date(raccolta.expires_at) < new Date();
  // "Completata" si legge dai documenti presenti, non dall'etichetta salvata:
  // se un documento viene cancellato, qui si vede subito che manca di nuovo.
  const completata = obbligatorie.length > 0 && fatte === obbligatorie.length;

  return (
    <div className="rounded-lg border border-[rgba(55,53,47,0.09)] dark:border-gray-700 p-2.5">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <FolderCheck className="h-4 w-4 flex-shrink-0 text-[#787774]" strokeWidth={1.5} />
          <span className="text-sm text-[#37352F] dark:text-white">
            {fatte} di {obbligatorie.length} consegnati
          </span>
          {completata && <span className="text-[11px] text-[#0F7B6C]">completata</span>}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onCopia}
            title="Copia il collegamento per il cliente"
            className="text-[#787774] hover:text-[#37352F] dark:hover:text-white p-1"
          >
            {copiato ? <Check className="h-3.5 w-3.5 text-[#0F7B6C]" /> : <Link2 className="h-3.5 w-3.5" />}
          </button>
          <button
            type="button"
            onClick={onElimina}
            title="Elimina la richiesta"
            className="text-[#787774] hover:text-red-500 p-1"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {scaduta && (
        <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-[#CB912F]">
          <Clock className="h-3 w-3" />
          Collegamento scaduto
          <button type="button" onClick={onRinnova} className="underline hover:text-[#37352F]">
            rinnova di 30 giorni
          </button>
        </div>
      )}

      <ul className="mt-2 space-y-1">
        {raccolta.voci.map((voce) => {
          const documento = raccolta.documenti.find((d) => d.requirement_id === voce.id);
          return (
            <li key={voce.id} className="flex items-center gap-2 text-[12px]">
              <span
                className={`flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-full border ${
                  documento
                    ? 'border-transparent bg-[#0F7B6C] text-white'
                    : 'border-[rgba(55,53,47,0.2)] text-transparent'
                }`}
              >
                <Check className="h-2 w-2" strokeWidth={3} />
              </span>

              <span className="min-w-0 flex-1 truncate text-[#37352F] dark:text-gray-200">
                {voce.label}
                {!voce.required && <span className="ml-1 text-[#9B9A97]">facoltativo</span>}
              </span>

              {documento ? (
                <>
                  <SignedUrl url={documento.url}>
                    {(href) => (
                      <a
                        href={href ?? undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="max-w-[110px] truncate text-[#337EA9] hover:underline"
                        title={
                          documento.created_at
                            ? `${documento.name} — consegnato il ${new Date(documento.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}`
                            : documento.name
                        }
                      >
                        {documento.name}
                      </a>
                    )}
                  </SignedUrl>
                  <button
                    type="button"
                    onClick={() => onVerifica(documento.id, !documento.verified_at)}
                    title={documento.verified_at ? 'Togli la verifica' : 'Segna come verificato'}
                    className={`rounded px-1.5 py-0.5 text-[10.5px] ${
                      documento.verified_at
                        ? 'bg-[#DBEDDB] text-[#0F7B6C]'
                        : 'text-[#787774] hover:bg-[rgba(55,53,47,0.06)]'
                    }`}
                  >
                    {documento.verified_at ? 'verificato' : 'verifica'}
                  </button>
                </>
              ) : (
                <span className="text-[11px] text-[#9B9A97]">manca</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
